import { useQuery } from 'hooks/useCustomQuery';
import getCompanyPlatforms from 'API/Company/getCompanyPlatforms';
import getCompanyPlatformNumbers from 'API/Company/getCompanyPlatformNumbers';
import ScopeType from 'utils/enums/ScopeType';

/**
 * How many numbers the company has on each of its platforms, for the Numbers
 * tile's breakdown. The numbers summary doesn't say which platform a number
 * is on, so this takes one request per platform, the same per-platform list
 * the Numbers page's platform filter shows. Requested at company scope, like
 * the rest of the dashboard.
 *
 * @param {string} companyId
 * @param {object} [options] - useQuery options; pass `enabled` to fetch only
 *   once the breakdown is wanted
 * @returns the query, with data as `{ platformID, name, count }[]`
 */
const useGetNumbersByPlatform = (companyId, options = {}) =>
    useQuery({
        queryKey: ['dashboardHome', 'numbersByPlatform', companyId],
        queryFn: async () => {
            const platforms = await getCompanyPlatforms(
                ScopeType.COMPANY,
                companyId,
            );
            return Promise.all(
                (platforms || []).map(async (platform) => {
                    const data = await getCompanyPlatformNumbers(
                        companyId,
                        platform.id,
                        ScopeType.COMPANY,
                    );
                    return {
                        platformID: platform.id,
                        name: platform.name,
                        count: data?.platformNumbers?.length ?? 0,
                    };
                }),
            );
        },
        refetchOnWindowFocus: false,
        retry: false,
        ...options,
        enabled: !!companyId && (options.enabled ?? true),
    });

export default useGetNumbersByPlatform;
